import React, { useContext } from 'react'
import { graphql } from 'react-apollo'
import ReactResizeDetector from 'react-resize-detector'
import MediaQuery from 'react-responsive'

import { AcornsIcon } from '@acorns/icons'
import { SignaturePad, withSignaturePad } from '@acorns/signature-pad'
import { Spinner } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { branch, compose, renderComponent, withHandlers, withState } from 'recompose'

import CenteredSpinner from 'components/CenteredSpinner'
import { CTAContainer } from 'components/CTAContainer'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { isMobile } from 'components/Trackers/platform'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ActionType, PageName, User } from 'generated/types'
import updateSignaturePoints from 'graphql-local/mutations/updateSignaturePoints.mutation'
import signaturePointsQuery from 'graphql-local/queries/signaturePoints.query'
import { useCardStatusRedirect } from 'pages/CardOrderReview/useCardStatusPolling'
import { TestIDs } from 'test/utils/testIds'
import Environment, { withEnv } from 'utils/environment'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext } from 'utils/FunnelContext'

import { useCardSignatureTracking } from './events'
import updateSignatureMutation from './gql/updateSignature.mutation.gql'
import userPhoneNumberQuery from './gql/userPhoneNumber.query.gql'
import PhoneModal from './PhoneModal'
import {
  HelperText,
  PageTitle,
  PhoneIcon,
  SignYourCardContainer,
  SignatureContainer,
  StyledButton,
  Subtitle,
  TextContainer,
  TextToPhoneButton,
} from './styled-components'

type Props = {
  phoneNumber: string | null
  isModalOpen: boolean
  setIsModalOpen: () => void
  setPhoneNumber: () => void
  env: Environment
}

type GQLProps = {
  loading: boolean
  user: User
}

type InnerProps = Props & GQLProps

const enhance = compose<InnerProps, {}>(
  withEnv,
  withFlowRouter,
  withState('phoneNumber', 'setPhoneNumber', null),
  withState('saving', 'setSaving', false),
  withState(
    'mobileLandscape',
    'setMobileLandscape',
    () => isMobile() && window.innerWidth > window.innerHeight,
  ),
  graphql(userPhoneNumberQuery, {
    options: { fetchPolicy: 'network-only' },
    props: ({ data: { user, loading } }) => {
      if (!loading) {
        return {
          phoneNumber: user ? user.phoneNumber : null,
        }
      }
    },
  }),
  withState('isModalOpen', 'setIsModalOpen', false),
  graphql(signaturePointsQuery, {
    props: ({ data: { signaturePoints } }: any) => ({
      signaturePoints,
    }),
  }),
  graphql(updateSignaturePoints, {
    props: ({ mutate }) => ({
      updatePoints: mutate,
    }),
  }),
  withSignaturePad(({ env, signaturePoints, updatePoints }) => ({
    signatureServiceUrl: env.get('signatureService'),
    signaturePoints,
    updatePoints,
  })),
  graphql(updateSignatureMutation, {
    props: ({ mutate }) => ({
      updateSignature: mutate,
    }),
  }),
  withHandlers({
    handleOpenModal: ({ setIsModalOpen, saving }) => () => {
      if (saving) {
        return
      } else {
        setIsModalOpen(true)
      }
    },
    handleCloseModal: ({ setIsModalOpen }) => () => {
      setIsModalOpen(false)
    },
    handleSubmitNumber: ({ handleNumberSubmit }) => () => {
      handleNumberSubmit()
    },
    onWindowResize: ({ setMobileLandscape }) => () => {
      if (isMobile() && window.innerWidth > window.innerHeight) {
        setMobileLandscape(true)
      } else {
        setMobileLandscape(false)
      }
    },
  }),
  branch(({ loading }) => loading, renderComponent(CenteredSpinner)),
)

const SignYourCard = props => {
  const { getRemoteString } = useRemoteStrings()
  const track = useCardSignatureTracking()
  const { handleCreateSvg, updateSignature, setSaving, route } = props
  const { isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)

  const nextStep = () => {
    isDynamicFunnelEnabled
      ? routeToPage(PageName.CardSignature, ActionType.Continue)
      : route({ from: FlowRoute.SignYourCard, action: 'continue' })
  }

  const loadingCardStatus = useCardStatusRedirect(nextStep)

  const handleNext = async () => {
    track.confirmTapped()
    const svgFile = await handleCreateSvg()

    setSaving(true)

    updateSignature({
      variables: {
        svgFile,
      },
    })
      .then(nextStep)
      .catch(() => {
        setSaving(false)
      })
  }

  if (loadingCardStatus) {
    return <CenteredSpinner />
  }

  return (
    <>
      <Nav back backClickTracker={TRACKER_IDS.CARD_SIGNATURE.BACK_BUTTON} />
      <Layout hideNavOnMobileLandscape mobileLandscapeSignaturePad={props.mobileLandscape}>
        <SignYourCardContainer>
          <TextContainer mobileLandscape={props.mobileLandscape}>
            <PageTitle>
              {getRemoteString({ key: 'registration.card_order.signature.header' })}
            </PageTitle>
            <Subtitle>
              {getRemoteString({ key: 'registration.moc_tier.card_order.signature.body' })}
            </Subtitle>
          </TextContainer>
          <SignatureContainer mobileLandscape={props.mobileLandscape}>
            <SignaturePad
              onRendered={props.setSignaturePad}
              onChange={props.handleChange}
              onClear={() => {
                track.clearTapped()
                props.handleClear()
              }}
              onEnd={props.handleEnd}
              onScale={props.handleScale}
              requireLandscape={false}
            />
          </SignatureContainer>
          <MediaQuery maxWidth={420}>
            <HelperText>You can rotate your phone for more space.</HelperText>
          </MediaQuery>
          {!isMobile() && (
            <MediaQuery minWidth={810}>
              <TextToPhoneButton
                onPress={props.handleOpenModal}
                secondary
                data-testid={TestIDs.SIGN_YOUR_CARD.SIGN_WITH_PHONE_BUTTON}
              >
                <PhoneIcon icon={AcornsIcon.Icon.UtilityMobilePhone} width={24} height={24} />
                {getRemoteString({ key: 'registration.card_order.signature.sign_on_phone.cta' })}
              </TextToPhoneButton>
            </MediaQuery>
          )}
        </SignYourCardContainer>
        <Separator />
        <CTAContainer>
          <StyledButton
            disabled={props.saving || props.isEmpty}
            onPress={handleNext}
            data-testid={TestIDs.SIGN_YOUR_CARD.NEXT_BUTTON}
          >
            {props.saving ? (
              <Spinner white height={20} width={20} />
            ) : (
              getRemoteString({ key: 'registration.card_order.signature.cta' })
            )}
          </StyledButton>
        </CTAContainer>
        <ReactResizeDetector
          handleWidth={true}
          handleHeight={true}
          onResize={props.onWindowResize}
        />
      </Layout>
      <PhoneModal
        isOpen={props.isModalOpen}
        onExit={props.handleCloseModal}
        onNumberSubmit={props.handleSubmitNumber}
        onRequestClose={props.handleModalClose}
        phoneNumber={props.phoneNumber}
        submittedPhoneNumber={props.submittedPhoneNumber}
        handleNumberSubmit={props.handleNumberSubmit}
      />
    </>
  )
}

export default enhance(SignYourCard)
